import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Field';
import { Alert } from '@/components/ui/Alert';
import { Card, CardBody } from '@/components/ui/Card';
import { useAuth } from '@/features/auth/useAuth';
import { isSupabaseConfigured } from '@/lib/supabase/client';
import { ROUTES } from '@/config/navigation';
import { cn } from '@/lib/cn';

type Role = 'customer' | 'partner' | 'seller' | 'provider';

export default function SignUpPage() {
  const { signUp } = useAuth();
  const [params] = useSearchParams();
  const requested = params.get('role');
  const [role, setRole] = useState<Role>(
    requested === 'partner' || requested === 'seller' || requested === 'provider'
      ? requested
      : 'customer',
  );
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await signUp({ email, password, firstName, lastName, phone, role });
      setSuccess(
        result.needsEmailConfirmation
          ? 'Compte créé. Vérifiez votre boîte e-mail pour confirmer votre adresse.'
          : 'Compte créé. Vous êtes connecté.',
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Inscription impossible.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="cf-container flex justify-center py-12">
      <div className="w-full max-w-lg">
        <h1 className="text-2xl font-semibold text-ink-950">Créer un compte</h1>
        <p className="mt-1 text-sm text-ink-600">
          Un compte client pour louer, un compte partenaire pour publier une flotte.
        </p>

        {!isSupabaseConfigured && (
          <Alert tone="warning" title="Supabase non configuré" className="mt-5">
            L’inscription nécessite un projet Supabase configuré.
          </Alert>
        )}

        <Card className="mt-5">
          <CardBody>
            {/* Quatre choix : deux colonnes sur téléphone, quatre sur écran
                large. Quatre boutons sur une seule ligne à 390 px donnerait
                des libellés coupés. */}
            <div className="mb-5 grid grid-cols-2 gap-2 rounded-xl bg-ink-100 p-1 sm:grid-cols-4">
              {(
                [
                  { value: 'customer', label: 'Je loue' },
                  { value: 'partner', label: 'Je suis une agence' },
                  { value: 'seller', label: 'Je vends ma voiture' },
                  { value: 'provider', label: 'Garage / lavage' },
                ] as const
              ).map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setRole(option.value)}
                  className={cn(
                    'rounded-lg px-3 py-2 text-sm font-medium text-ink-600',
                    role === option.value && 'bg-white text-ink-950 shadow-card',
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  label="Prénom"
                  required
                  autoComplete="given-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
                <Input
                  label="Nom"
                  required
                  autoComplete="family-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>

              <Input
                label="Téléphone"
                type="tel"
                required
                placeholder="+225 07 00 00 00 00"
                autoComplete="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
              <Input
                label="Adresse e-mail"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Input
                label="Mot de passe"
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                hint="8 caractères minimum."
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />

              {role === 'seller' && (
                <Alert tone="info">
                  Vos annonces sont vérifiées par l’administration avant leur mise en ligne. La
                  publication est gratuite : aucun frais, aucune commission sur votre vente.
                </Alert>
              )}

              {role === 'provider' && (
                <Alert tone="info">
                  Votre fiche est vérifiée par l’administration avant sa mise en ligne : nous
                  appelons le numéro que vous indiquez. L’inscription est gratuite et sans
                  commission — vos clients vous règlent directement, sur place.
                </Alert>
              )}

              {role === 'partner' && (
                <Alert tone="info">
                  Un compte agence doit être vérifié par l’administration avant que ses véhicules
                  soient visibles publiquement. Les documents de société vous seront demandés dans l’onglet Documents de votre espace.
                </Alert>
              )}

              {error && <Alert tone="danger">{error}</Alert>}
              {success && <Alert tone="success">{success}</Alert>}

              <Button
                type="submit"
                size="lg"
                className="w-full"
                loading={loading}
                disabled={!isSupabaseConfigured}
              >
                Créer mon compte
              </Button>
            </form>
          </CardBody>
        </Card>

        <p className="mt-5 text-center text-sm text-ink-600">
          Déjà inscrit ?{' '}
          <Link to={ROUTES.signIn} className="font-medium text-brand-700 hover:underline">
            Se connecter
          </Link>
        </p>
      </div>
    </div>
  );
}
